// Equipment detail page. Reached via /equipment/:id.

import React, {useEffect, useState} from 'react'
import clsx from 'clsx'
import {useNavigate, useParams} from 'react-router-dom'
import {Wrench} from 'lucide-react'
import {Header} from '../../components/Header'
import {PageShell} from '../../components/PageShell'
import {TagBadge} from '../../components/common/TagBadge'
import {showToast} from '../../components/common/Toast'
import {supabase} from '../../app/supabaseClient'
import type {EquipmentDetail, EquipmentStatus, EquipmentTag} from './types'

const STATUS_LABEL: Record<EquipmentStatus, string> = {
  active: 'Active',
  down: 'Down',
  maintenance: 'Maintenance',
  idle: 'Idle',
}

const STATUS_PILL_CLASS: Record<EquipmentStatus, string> = {
  active:      'bg-badgeGreenBg text-statusGreen',
  down:        'bg-badgeRedBg text-statusRed',
  maintenance: 'bg-badgeYellowBg text-statusYellow',
  idle:        'bg-surfaceLight text-textMuted',
}

export const EquipmentDetailScreen: React.FC = () => {
  const {id} = useParams<{id: string}>()
  const navigate = useNavigate()
  const [eq, setEq] = useState<EquipmentDetail | null>(null)
  const [tags, setTags] = useState<EquipmentTag[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const [{data: e, error: ee}, {data: t, error: te}] = await Promise.all([
        supabase
          .from('equipment')
          .select('id, name, model, asset_id, activity, status')
          .eq('id', id)
          .single(),
        supabase
          .from('tags')
          .select('id, tag_id, battery_level, signal_strength, is_connected')
          .eq('assigned_to_id', id)
          .eq('assigned_to_type', 'equipment'),
      ])
      if (cancelled) return
      if (ee) setError(ee.message)
      else setEq(e as EquipmentDetail)
      if (te) setError(te.message)
      else setTags((t as EquipmentTag[]) ?? [])
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [id])

  const handleUnassignTag = async (tag: EquipmentTag) => {
    if (!window.confirm(`Unassign tag #${tag.tag_id}?`)) return
    const {error: e} = await supabase
      .from('tags')
      .update({assigned_to_id: null, assigned_to_type: null})
      .eq('id', tag.id)
    if (e) {
      showToast(`Unassign failed: ${e.message}`, 'error')
      return
    }
    setTags(prev => prev.filter(t => t.id !== tag.id))
    showToast('Tag unassigned.', 'success')
  }

  return (
    <main className="flex flex-1 flex-col overflow-hidden">
      <Header title={eq?.asset_id ?? 'Equipment'} backTo="/equipment" />
      <PageShell
        footer={
          eq && (
            <button
              type="button"
              onClick={() => navigate(`/equipment/${eq.id}/edit`)}
              className="rounded-md border border-primary px-lg py-sm text-md font-semibold text-primary hover:bg-primaryBg"
            >
              Edit
            </button>
          )
        }
      >
        {loading ? (
          <div className="flex h-full items-center justify-center text-textMuted">Loading…</div>
        ) : error || !eq ? (
          <div className="rounded-lg border border-statusRed bg-surfaceCard p-xxl">
            <h3 className="mb-md text-lg font-semibold text-statusRed">Fetch failed</h3>
            <pre className="whitespace-pre-wrap font-mono text-sm text-textPrimary">{error ?? 'Not found'}</pre>
          </div>
        ) : (
          <div className="flex flex-col gap-xl">
            <div className="flex items-center gap-lg">
              <div className="flex h-16 w-16 items-center justify-center rounded-md bg-surfaceLight">
                <Wrench size={32} color="var(--color-primary)" />
              </div>
              <div>
                <div className="text-xxl font-bold text-textPrimary">{eq.asset_id}</div>
                <div className="text-md text-textMuted">{eq.name} · {eq.model}</div>
                {eq.activity && (
                  <div className="mt-xs text-sm text-textSecondary">Activity: {eq.activity}</div>
                )}
              </div>
              <div className="ml-auto">
                <span
                  className={clsx(
                    'rounded-round px-md py-xs text-xs font-semibold uppercase',
                    STATUS_PILL_CLASS[eq.status],
                  )}
                >
                  {STATUS_LABEL[eq.status]}
                </span>
              </div>
            </div>

            <dl className="grid grid-cols-2 gap-x-xl gap-y-md rounded-md border border-surfaceBorder bg-surfaceCard p-lg">
              <ViewField label="OEM / Name" value={eq.name} />
              <ViewField label="Model" value={eq.model} />
              <ViewField label="Asset ID" value={eq.asset_id} />
              <ViewField label="Activity" value={eq.activity ?? '—'} />
              <ViewField label="Status" value={STATUS_LABEL[eq.status]} />
            </dl>

            <div className="rounded-md border border-surfaceBorder bg-surfaceCard p-lg">
              <div className="mb-md text-sm font-semibold uppercase tracking-wider text-textMuted">
                Assigned Tags ({tags.length})
              </div>
              {tags.length === 0 ? (
                <div className="text-sm text-textMuted">
                  No tags assigned. Pair via BLE in the mobile app.
                </div>
              ) : (
                <ul className="flex flex-col gap-sm">
                  {tags.map(t => (
                    <li
                      key={t.id}
                      className="flex items-center gap-md rounded-md bg-surface px-md py-sm"
                    >
                      <TagBadge tagId={t.tag_id} isActive={t.is_connected} size="medium" />
                      <span className="text-sm text-textSecondary">
                        Battery {t.battery_level}% · {t.signal_strength}
                      </span>
                      <span className="ml-auto text-xs text-textMuted">
                        {t.is_connected ? 'Connected' : 'Disconnected'}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleUnassignTag(t)}
                        className="rounded-md border border-statusRed px-md py-xs text-xs text-statusRed hover:bg-statusRed hover:text-white"
                      >
                        Unassign
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </PageShell>
    </main>
  )
}

const ViewField: React.FC<{label: string; value: string}> = ({label, value}) => (
  <div>
    <dt className="text-xs uppercase tracking-wider text-textMuted">{label}</dt>
    <dd className="mt-1 text-md text-textPrimary">{value}</dd>
  </div>
)
